export type ResizeDirectionOptions = {
  minWidth?: number
  maxWidth?: number
  /** `displayWidth` is clamped; `rawWidth` is the unclamped drag width (for overdrag). */
  onResize?: (displayWidthPx: number, rawWidthPx: number) => void
  onResizeEnd?: (displayWidthPx: number, rawWidthPx: number) => void
  onReset?: () => void
}

const clampWidth = (width: number, options: ResizeDirectionOptions) => {
  let next = width
  if (options.minWidth != null) next = Math.max(options.minWidth, next)
  if (options.maxWidth != null) next = Math.min(options.maxWidth, next)
  return next
}

export const directionHandler = (
  el: HTMLElement,
  direction: 'ltr' | 'rtl',
  resizerStyles: { [key: string]: string },
  options: ResizeDirectionOptions = {},
) => {
  el.style.position = 'relative'
  const resizer = document.createElement('div')
  const resizeLine = document.createElement('div')
  Object.keys(resizerStyles).forEach((key: string) => {
    resizer.style.setProperty(key, resizerStyles[key]!)
  })
  el.appendChild(resizer)
  resizer.classList.add('bg-transparent')
  resizer.dataset.resizeHandle = 'true'
  if (el.getAttribute('dark')) {
    resizeLine.classList.add('bg-[#202C36]')
  } else {
    resizeLine.classList.add('bg-bor-nav')
  }
  resizeLine.style.width = '1px'
  resizer.appendChild(resizeLine)
  resizer.style.cursor = 'ew-resize'

  let startX = 0
  let startWidth = 0
  let lastRawWidth = 0

  const doDrag = (e: MouseEvent) => {
    document.body.style.userSelect = 'none'
    const raw = direction === 'ltr'
      ? startWidth + e.clientX - startX
      : startWidth - e.clientX + startX
    lastRawWidth = raw
    const next = clampWidth(raw, options)
    el.style.width = `${next}px`
    options.onResize?.(next, raw)
  }

  const stopDrag = () => {
    document.body.style.userSelect = 'auto'
    document.documentElement.removeEventListener('mousemove', doDrag, false)
    document.documentElement.removeEventListener('mouseup', stopDrag, false)
    const width = Number.parseInt(el.style.width, 10)
    if (Number.isFinite(width)) options.onResizeEnd?.(width, lastRawWidth)
  }

  const startDrag = (e: MouseEvent) => {
    startX = e.clientX
    if (!document.defaultView) return
    startWidth = Number.parseInt(document.defaultView.getComputedStyle(el).width, 10)
    lastRawWidth = startWidth
    document.documentElement.addEventListener('mousemove', doDrag, false)
    document.documentElement.addEventListener('mouseup', stopDrag, false)
  }

  const onDblClick = () => {
    options.onReset?.()
  }

  resizer.addEventListener('mousedown', startDrag, false)
  resizer.addEventListener('dblclick', onDblClick, false)
}
